/**
 * pplTalk Cloud Function service.
 * Wraps all httpsCallable calls for pplTalk interactions (post, like, reply, flag).
 * All mutating functions require authentication — the Cloud Functions validate auth server-side.
 */

import { httpsCallable } from 'firebase/functions';
import { FIREBASE_FUNCTIONS } from '../firebaseConfig';
import type {
  TalkValidatorResponse,
  LikeMessageResponse,
  ReplyValidatorResponse,
  FlagMessageResponse,
} from '../types/pplTalk';

// ─── Callable references ────────────────────────────────────────────────────

const talkValidatorFn = httpsCallable<
  { title: string; body: string | null; categories: string[]; userName: string; userAvatar: string | null },
  TalkValidatorResponse
>(FIREBASE_FUNCTIONS, 'talkValidatorEurW');

const likeMessageFn = httpsCallable<
  { messageId: string; action: 'add' | 'remove' },
  LikeMessageResponse
>(FIREBASE_FUNCTIONS, 'likeMessageEurW');

const likeReplyFn = httpsCallable<
  { messageId: string; replyId: string; action: 'add' | 'remove' },
  LikeMessageResponse
>(FIREBASE_FUNCTIONS, 'likeReplyEurW');

const replyValidatorFn = httpsCallable<
  { text: string; messageId: string; userName: string; userAvatar: string | null },
  ReplyValidatorResponse
>(FIREBASE_FUNCTIONS, 'replyValidatorEurW');

const flagMessageFn = httpsCallable<
  { messageId: string },
  FlagMessageResponse
>(FIREBASE_FUNCTIONS, 'flagMessageEurW');

// ─── Exported service functions ─────────────────────────────────────────────

export interface CreatePostParams {
  title: string;
  body?: string;
  categories: string[];
  userName: string;
  userAvatar?: string | null;
}

export async function createPost(params: CreatePostParams): Promise<TalkValidatorResponse> {
  const result = await talkValidatorFn({
    title: params.title.trim(),
    body: params.body?.trim() || null,
    categories: params.categories,
    userName: params.userName,
    userAvatar: params.userAvatar ?? null,
  });
  return result.data;
}

export async function likeMessage(
  messageId: string,
  action: 'add' | 'remove'
): Promise<LikeMessageResponse> {
  const result = await likeMessageFn({ messageId, action });
  return result.data;
}

export async function likeReply(
  messageId: string,
  replyId: string,
  action: 'add' | 'remove'
): Promise<LikeMessageResponse> {
  const result = await likeReplyFn({ messageId, replyId, action });
  return result.data;
}

export interface CreateReplyParams {
  text: string;
  messageId: string;
  userName: string;
  userAvatar?: string | null;
}

export async function createReply(params: CreateReplyParams): Promise<ReplyValidatorResponse> {
  const result = await replyValidatorFn({
    text: params.text.trim(),
    messageId: params.messageId,
    userName: params.userName,
    userAvatar: params.userAvatar ?? null,
  });
  return result.data;
}

export async function flagMessage(messageId: string): Promise<FlagMessageResponse> {
  const result = await flagMessageFn({ messageId });
  return result.data;
}
